import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface TrackerData {
  [key: string]: any;
}

export function useTrackerData() {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const saveTrackerData = useCallback(async (
    tableName: string,
    data: TrackerData,
    showToast = false
  ) => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        if (showToast) {
          toast({
            title: "Authentication required",
            description: "Please log in to save your data.",
            variant: "destructive"
          });
        }
        return false;
      }

      const today = new Date().toISOString().split('T')[0];
      
      // Clean undefined values to prevent database errors
      const cleanedData = Object.entries(data).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as TrackerData);
      
      const updateData = {
        user_id: user.id,
        date: today,
        ...cleanedData,
        updated_at: new Date().toISOString()
      };

      const { error } = await (supabase as any)
        .from(tableName)
        .upsert(updateData, { 
          onConflict: 'user_id,date',
          ignoreDuplicates: false 
        });

      if (error) {
        console.error(`Error saving ${tableName} data:`, error);
        if (showToast) {
          toast({
            title: "Could not save",
            description: error.message || "Please try again or contact support.",
            variant: "destructive"
          });
        }
        return false;
      }

      if (showToast) {
        toast({
          title: "Saved!",
          description: "Your data has been saved successfully."
        });
      }

      return true;
    } catch (error: any) {
      console.error(`Error in saveTrackerData for ${tableName}:`, error);
      if (showToast) {
        toast({
          title: "Unexpected error",
          description: error.message || "Please try again.",
          variant: "destructive"
        });
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const loadTrackerData = useCallback(async (
    tableName: string,
    dateFilter?: string
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const today = dateFilter || new Date().toISOString().split('T')[0];
      
      const { data, error } = await (supabase as any)
        .from(tableName)
        .select('*')
        .eq('user_id', user.id)
        .eq('date', today)
        .maybeSingle();

      if (error) {
        console.error(`Error loading ${tableName} data:`, error);
        return null;
      }

      return data;
    } catch (error) {
      console.error(`Error in loadTrackerData for ${tableName}:`, error);
      return null;
    }
  }, []);

  const loadLatestCycleData = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await (supabase as any)
        .from('daily_checkins')
        .select('cycle_start_date, cycle_length, period_end_date, date')
        .eq('user_id', user.id)
        .not('cycle_start_date', 'is', null)
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error loading latest cycle data:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in loadLatestCycleData:', error);
      return null;
    }
  }, []);

  const getHistoricalData = useCallback(async (
    tableName: string,
    days: number = 30
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - days);

      const { data, error } = await (supabase as any)
        .from(tableName)
        .select('*')
        .eq('user_id', user.id)
        .gte('date', startDate.toISOString().split('T')[0])
        .lte('date', endDate.toISOString().split('T')[0])
        .order('date', { ascending: false });

      if (error) {
        console.error(`Error loading historical ${tableName} data:`, error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error(`Error in getHistoricalData for ${tableName}:`, error);
      return [];
    }
  }, []);

  return {
    saveTrackerData,
    loadTrackerData,
    loadLatestCycleData,
    getHistoricalData,
    isLoading
  };
}